import {useQuery} from '@tanstack/react-query';
import {getPointsPersonalSummary} from '@/services/points';
import {pointsKeys} from '@/hooks/points/queryKeys';
import {queryClient} from '@/utils/queryClient';
import type {PointsPersonalSummaryData} from '@/services/points/interface';
import {useAuthStore} from '@/store/useAuthStore';

/**
 * 使个人积分概览缓存失效并立即 refetch
 */
export const invalidatePointsSummary = () =>
    queryClient.invalidateQueries({queryKey: pointsKeys.personalSummary()});

/**
 * 获取当前用户个人积分概览
 *
 * @example
 * ```tsx
 * // 读取积分概览
 * const { summary, isLoading } = usePointsSummary();
 *
 * // 消耗积分后刷新
 * await createProject(params);
 * invalidatePointsSummary();
 * ```
 */
export const usePersonalPoints = () => {
    const isLogin = useAuthStore((state) => state.isLogin);

    const query = useQuery<PointsPersonalSummaryData>({
        queryKey: pointsKeys.personalSummary(),
        queryFn: getPointsPersonalSummary,
        staleTime: 1000 * 30,
        enabled: isLogin,
    });

    return {
        ...query,
        /** 完整积分概览数据 */
        summary: query.data,
        /** 个人余额 */
        personalTotalRemaining: query.data?.personalTotalRemaining,
    };
};
